import {MOON_250} from '@wandb/weave/common/css/globals.styles';
import {Icon} from '@wandb/weave/components/Icon/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useMemo} from 'react';
import {CellInfo} from 'react-table';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {entity as entityUrl, queue as queueUrl} from '../../../util/urls';
import {Link} from '../Link';
import {ResourceChip} from '../ResourceChip';
import {Table} from '../Table';
import {AgentStatuses} from '../utilTypes';
import {ActiveChip} from './ActiveChip';
import {enhanceRunQueueItems, Queue} from './queueUtil';

type QueuesTableProps = {
  queues: Queue[];
  runInfoMap: {[key: string]: any};
  onDeleteQueue: (queue: Queue) => void;
};

const Centered = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const QueuesTable = ({queues, runInfoMap}: QueuesTableProps) => {
  const rows = useMemo(() => {
    return queues.map(queue => {
      const initialAccumulator = {
        itemsQueued: 0,
        totalItems: 0,
        itemsRunning: 0,
        finishedRuns: 0,
        crashedRuns: 0,
        authors: new Set(),
      };

      // Get run info to fetch last successful run time
      if (runInfoMap != null) {
        queue.items = enhanceRunQueueItems(queue.items, queue, runInfoMap);
      }

      const result = queue.items.reduce((accumulator, item) => {
        accumulator.totalItems++;
        if (item.state === 'PENDING') {
          accumulator.itemsQueued++;
        }

        if (item.run != null) {
          if (item.run.state === 'running') {
            accumulator.itemsRunning++;
          } else if (item.run.state === 'finished') {
            accumulator.finishedRuns++;
          } else if (
            item.run.state === 'crashed' ||
            item.run.state === 'failed'
          ) {
            accumulator.crashedRuns++;
          }
        }
        accumulator.authors.add(item.author);
        return accumulator;
      }, initialAccumulator);

      return {
        searchString: `${queue.name}|${queue.entityName}|${queue.resourceType}|${queue.status}`,
        row: {
          ...queue,
          itemsQueued: result.itemsQueued,
          totalItems: result.totalItems,
          itemsRunning: result.itemsRunning,
          finishedRuns: result.finishedRuns,
          crashedRuns: result.crashedRuns,
          activeUsers: result.authors.size,
          numAgents: queue.agents.filter(a =>
            [
              AgentStatuses.Polling as string,
              AgentStatuses.Running as string,
            ].includes(a.agentStatus)
          ).length,
          agentHeartbeat: queue.agents
            .filter(x => x.heartbeatAt != null)
            .sort(
              (a, b) =>
                new Date(b.heartbeatAt!).getTime() -
                new Date(a.heartbeatAt!).getTime()
            )[0]?.heartbeatAt,
          lastSuccessfulRun: queue.items
            .filter(x => x.run != null && x.run.state === 'finished')
            .sort(
              (a, b) =>
                new Date(b.updatedAt!).getTime() -
                new Date(a.updatedAt!).getTime()
            )[0]?.updatedAt,
        },
      };
    });
  }, [queues, runInfoMap]);

  const columns = [
    {
      Header: 'Queue',
      accessor: 'name',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <Link
            to={queueUrl(cellInfo.original.entityName, cellInfo.original.id)}>
            {cellInfo.value}
          </Link>
        );
      },
    },
    {
      Header: 'State',
      accessor: 'status',
      resizable: true,
      minWidth: 70,
      Cell: (cellInfo: CellInfo) => {
        const heartbeat = cellInfo.original.agentHeartbeat
          ? new Date(cellInfo.original.agentHeartbeat).toLocaleString()
          : 'N/A';
        return (
          <Tooltip
            position="top center"
            content={'Last agent heartbeat: ' + heartbeat}
            trigger={
              <div>
                <ActiveChip status={cellInfo.value} />
              </div>
            }
          />
        );
      },
    },
    {
      Header: 'Entity',
      accessor: 'entityName',
      resizable: true,
      minWidth: 80,
      Cell: (cellInfo: CellInfo) => {
        return <Link to={entityUrl(cellInfo.value)}>{cellInfo.value}</Link>;
      },
    },
    {
      Header: 'Resource Type',
      accessor: 'resourceType',
      resizable: true,
      minWidth: 75,
      Cell: (cellInfo: CellInfo) => {
        return <ResourceChip value={cellInfo.value} />;
      },
    },
    {
      Header: 'Prioritization',
      accessor: 'prioritizationMode',
      resizable: true,
      minWidth: 35,
      Cell: (cellInfo: CellInfo) => {
        switch (cellInfo.value) {
          case 'DISABLED':
            return '';
          case 'V0':
            return (
              <Centered>
                <Icon name="checkmark" />
              </Centered>
            );
        }

        return '';
      },
    },
    {
      Header: 'Agents',
      accessor: 'numAgents',
      resizable: true,
      minWidth: 40,
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value;
      },
    },
    {
      Header: 'Queued runs',
      accessor: 'itemsQueued',
      resizable: true,
      minWidth: 55,
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value;
      },
    },
    {
      Header: 'Running runs',
      accessor: 'itemsRunning',
      resizable: true,
      minWidth: 40,
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value;
      },
    },
    {
      Header: 'Last successful run',
      accessor: 'lastSuccessfulRun',
      resizable: true,
      minWidth: 100,
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value
          ? new Date(cellInfo.value).toLocaleString()
          : 'N/A';
      },
    },
    {
      Header: 'Active users',
      accessor: 'activeUsers',
      resizable: true,
      minWidth: 55,
      Cell: (cellInfo: CellInfo) => {
        return cellInfo.value;
      },
    },
  ];
  const defaultSorted = [{id: 'status', desc: false}];
  return (
    <Table
      border={`1px solid ${MOON_250}`}
      data={rows}
      columns={columns}
      pageSize={20}
      defaultSorted={defaultSorted}
    />
  );
};
